import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { formatPrice, calculateDiscount } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Star } from 'lucide-react';

export const Wishlist = () => {
  const { wishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-12 h-12 text-muted-foreground" />
          </div>
          <h2 className="font-serif text-3xl font-bold mb-4">Your wishlist is empty</h2>
          <p className="text-muted-foreground mb-8">Save products you love for later</p>
          <Link to="/shop">
            <Button size="lg">Start Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-2">
            My Wishlist
          </h1>
          <p className="text-muted-foreground mb-8">
            {wishlist.length} item{wishlist.length !== 1 ? 's' : ''} saved
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {wishlist.map((product, index) => {
              const finalPrice = product.discount > 0
                ? calculateDiscount(product.price, product.discount)
                : product.price;

              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group overflow-hidden h-full flex flex-col">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <Link to={`/product/${product.id}`}>
                        {product.images && product.images[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-4xl text-muted-foreground">NC</span>
                          </div>
                        )}
                      </Link>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-3 right-3"
                        onClick={() => removeFromWishlist(product.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      {product.discount > 0 && (
                        <Badge variant="destructive" className="absolute top-3 left-3">
                          -{product.discount}%
                        </Badge>
                      )}
                    </div>

                    <div className="p-4 flex-1 flex flex-col">
                      <Link to={`/product/${product.id}`}>
                        <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>
                        <h3 className="font-serif text-lg font-semibold leading-tight mb-2 line-clamp-2 hover:text-primary transition-smooth">
                          {product.name}
                        </h3>
                      </Link>

                      {product.rating && (
                        <div className="flex items-center gap-1 mb-3">
                          <Star className="w-4 h-4 fill-primary text-primary" />
                          <span className="text-sm font-semibold">{product.rating}</span>
                        </div>
                      )}

                      <div className="mt-auto">
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-xl font-bold text-primary">
                            {formatPrice(finalPrice)}
                          </span>
                          {product.discount > 0 && (
                            <span className="text-sm text-muted-foreground line-through">
                              {formatPrice(product.price)}
                            </span>
                          )}
                        </div>

                        <Button
                          className="w-full"
                          onClick={() => {
                            addToCart(product);
                            removeFromWishlist(product.id);
                          }}
                          disabled={product.stock_quantity <= 0}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Move to Cart
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};